import React from 'react'
import { AppLayout } from '../../layouts/AppLayout'
import { PageHeader } from '../../layouts/PageHeader'
import { useTheme } from '../../../hooks/useTheme'
import { useProjectAnalysisOverview } from '../../../hooks/use-project-analysis-overview'
import { PROJECT_ANALYSIS_PAGE_TITLE } from './constants'
import { ProjectOverviewLoading } from './project-overview-loading'
import { ProjectOverviewError } from './project-overview-error'
import { ProjectOverviewFilters } from './project-overview-filters'
import { ProjectOverviewKpiStrip } from './project-overview-kpi-strip'
import { ProjectOverviewChartsSection } from './project-overview-charts-section'
import { ProjectOverviewTable } from './project-overview-table'
import { appPath } from '../../../utils/appPath'

export const ProjectAnalysisPage: React.FC = () => {
  const { isDark, mounted } = useTheme()
  const o = useProjectAnalysisOverview()

  if (!mounted) return null
  if (o.loading) return <ProjectOverviewLoading />
  if (o.error)
    return <ProjectOverviewError message={o.error} onRetry={o.refetch} />

  const showTableSubtitle =
    Boolean(o.orgCategoryFilter || o.orgPeriodKeys?.length) &&
    Boolean(o.displaySummary?.subtitle)

  return (
    <AppLayout>
      <PageHeader
        title={PROJECT_ANALYSIS_PAGE_TITLE}
        filters={
          <ProjectOverviewFilters
            orgCategoryFilter={o.orgCategoryFilter}
            onCategoryChange={o.setOrgCategoryFilter}
            categoryOptionValues={o.categoryOptions}
            orgFilterMode={o.orgFilterMode}
            onOrgFilterModeChange={o.setOrgFilterMode}
            orgFY={o.orgFY}
            onOrgFYChange={o.setOrgFY}
            orgQ={o.orgQ}
            onOrgQChange={o.setOrgQ}
            orgYear={o.orgYear}
            onOrgYearChange={o.setOrgYear}
            orgMonth={o.orgMonth}
            onOrgMonthChange={o.setOrgMonth}
            orgFYOptions={o.orgFYOptions}
            orgYearOptions={o.orgYearOptions}
          />
        }
      />

      <div className="p-6 max-w-[1600px] space-y-6 pb-12">
        {o.displaySummary && (
          <ProjectOverviewKpiStrip displaySummary={o.displaySummary} />
        )}

        <ProjectOverviewChartsSection
          isDark={isDark}
          displaySummary={o.displaySummary}
          overviewHoursPieData={o.overviewHoursPieData}
          overviewTopProjectsBarData={o.overviewTopProjectsBarData}
          overviewUtilDistribution={o.overviewUtilDistribution}
        />

        <ProjectOverviewTable
          displayedProjects={o.displayedProjects}
          rowsForExport={o.filteredAndSortedProjects}
          remainingCount={o.remainingCount}
          onLoadMore={() => o.setDisplayCount((c) => c + 25)}
          tableSubtitle={
            o.displaySummary?.subtitle != null
              ? String(o.displaySummary.subtitle)
              : null
          }
          showTableSubtitle={showTableSubtitle}
          search={o.search}
          onSearchChange={o.setSearch}
          sortBy={o.sortBy}
          sortOrder={o.sortOrder}
          onToggleSort={o.toggleSort}
          exportLabelParts={o.exportLabelParts}
          onRowNavigate={(id) =>
            o.navigate(appPath(`/project-analysis/${id}`))
          }
        />
      </div>
    </AppLayout>
  )
}

export default ProjectAnalysisPage
